import type {
  VolunteerOrgProjectAssignment,
  VolunteerOrganization,
} from "@entities";
import { AdvancedMarker, APIProvider, Pin } from "@vis.gl/react-google-maps";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { hasValidCoordinates } from "@shared/helpers/geo";
import {
  volunteerOrgDisplayName,
  volunteerOrgKindLabel,
} from "@shared/helpers/volunteer-organization";
import { useMemo, useState } from "react";
import GenericMap from "../../../components/mission-map/GenericMap";
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LANGUAGE,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_REGION,
} from "../../../constants/google-maps-browser";
import { DEFAULT_CENTER } from "../../mission-map/constants";

type AssignmentPin = {
  id: string;
  coordinates: { lat: number; lng: number };
  address: string;
  volunteerCount: number;
  org: VolunteerOrganization | undefined;
  source: "assignment" | "organization";
};

function assignmentPinCoordinates(
  row: VolunteerOrgProjectAssignment,
): { coordinates: { lat: number; lng: number }; source: AssignmentPin["source"] } | null {
  if (hasValidCoordinates(row.coordinates)) {
    return { coordinates: row.coordinates, source: "assignment" };
  }
  const org = row.volunteerOrganization;
  if (hasValidCoordinates(org?.coordinates)) {
    return { coordinates: org.coordinates, source: "organization" };
  }
  return null;
}

export function VolunteerOrgProjectAssignmentsMap({
  projectName,
  assignments,
  onOpenOrganization,
}: {
  projectName: string;
  assignments: VolunteerOrgProjectAssignment[];
  onOpenOrganization?: (org: VolunteerOrganization) => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const pins = useMemo(() => {
    const result: AssignmentPin[] = [];
    for (const row of assignments) {
      const pin = assignmentPinCoordinates(row);
      if (!pin) continue;
      result.push({
        id: row.id,
        coordinates: pin.coordinates,
        address: row.address || row.volunteerOrganization?.address || "",
        volunteerCount: row.volunteerCount,
        org: row.volunteerOrganization,
        source: pin.source,
      });
    }
    return result;
  }, [assignments]);

  const missingCount = assignments.length - pins.length;
  const selected = pins.find((pin) => pin.id === selectedId) ?? null;
  const defaultCenter = pins[0]?.coordinates ?? DEFAULT_CENTER;

  return (
    <Box
      sx={{
        minHeight: 360,
        height: { xs: 360, lg: "70vh" },
        borderRadius: 2.5,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        position: "relative",
      }}
    >
      {pins.length === 0 ? (
        <Alert severity="info" sx={{ m: 2 }}>
          לפרויקט {projectName} יש שיוכים, אבל אין נקודה במפה (לא בשיוך ולא בארגון).
        </Alert>
      ) : (
        <APIProvider
          apiKey={GOOGLE_MAPS_API_KEY}
          libraries={GOOGLE_MAPS_LIBRARIES}
          language={GOOGLE_MAPS_LANGUAGE}
          region={GOOGLE_MAPS_REGION}
        >
          {missingCount > 0 ? (
            <Alert
              severity="info"
              sx={{
                position: "absolute",
                top: 8,
                insetInlineEnd: 8,
                zIndex: 3,
                maxWidth: 280,
              }}
            >
              {missingCount} שיוכים בלי נקודה במפה
            </Alert>
          ) : null}
          <GenericMap
            defaultCenter={defaultCenter}
            defaultZoom={pins.length === 1 ? 12 : 8}
            style={{ width: "100%", height: "100%" }}
          >
            {pins.map((pin) => (
              <AdvancedMarker
                key={pin.id}
                position={pin.coordinates}
                onClick={() => setSelectedId(pin.id)}
                title={
                  pin.org
                    ? volunteerOrgDisplayName(pin.org.name, pin.org.parent?.name)
                    : pin.address
                }
              >
                <Pin
                  background={selectedId === pin.id ? "#05204C" : "#00b4d8"}
                  borderColor="#fff"
                  glyphColor="#fff"
                />
              </AdvancedMarker>
            ))}
          </GenericMap>
          {selected ? (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: 16,
                insetInlineStart: 16,
                p: 2,
                maxWidth: 320,
                zIndex: 2,
                borderRadius: "12px",
              }}
            >
              <Stack gap={0.75}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {selected.org
                    ? volunteerOrgDisplayName(
                        selected.org.name,
                        selected.org.parent?.name,
                      )
                    : "שיוך"}
                </Typography>
                {selected.org ? (
                  <Typography variant="caption" color="text.secondary">
                    {volunteerOrgKindLabel(selected.org)}
                    {selected.source === "organization"
                      ? " · נקודה מהארגון"
                      : " · נקודת השיוך"}
                  </Typography>
                ) : null}
                <Typography variant="body2" color="text.secondary">
                  {selected.address || "ללא כתובת"}
                </Typography>
                <Typography variant="body2">
                  {selected.volunteerCount} מתנדבים
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {selected.org && onOpenOrganization ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => onOpenOrganization(selected.org!)}
                    >
                      כרטיס ארגון
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => setSelectedId(null)}>
                    סגור
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ) : null}
        </APIProvider>
      )}
    </Box>
  );
}
